/**
 * CountrySelector Component
 * Reusable country selector with ISO 3166-1 alpha-2 codes, i18n support, and priority ordering
 *
 * Features:
 * - 196 countries with ISO codes
 * - Priority countries: Switzerland (CH), Germany (DE), Austria (AT) always first
 * - Full i18n support for EN, DE, FR, IT
 * - React Hook Form integration
 * - Accessibility compliant (WCAG 2.1 AA)
 * - Shadcn/ui Select component
 * - Optimized with useMemo for performance
 *
 * @example
 * ```tsx
 * <CountrySelector
 *   value={formData.country}
 *   onValueChange={(value) => setFormData({ ...formData, country: value })}
 *   required
 *   placeholder="Select your country"
 * />
 * ```
 */

import { useMemo } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCountrySelector } from '@/hooks/useCountrySelector';
import { useFormsTranslation } from '@/hooks/useTranslation';
import type { CountrySelectorProps } from '@/types/country.types';

/**
 * CountrySelector Component
 * Renders a searchable dropdown for country selection with i18n support
 *
 * @param props - Component props following CountrySelectorProps interface
 * @returns React component for country selection
 */
export function CountrySelector({
  value,
  onValueChange,
  disabled = false,
  placeholder,
  className,
  error,
  required = false,
  name,
  id,
  autoFocus = false,
}: CountrySelectorProps) {
  const { t: tForms } = useFormsTranslation();
  const { getCountryOptions, getCountryName } = useCountrySelector();

  // Memoize country options to prevent recalculation on every render
  const countryOptions = useMemo(
    () => getCountryOptions(),
    [getCountryOptions]
  );

  // Memoize placeholder to prevent unnecessary recalculations
  const defaultPlaceholder = useMemo(
    () => placeholder || tForms('placeholders.selectCountry'),
    [placeholder, tForms]
  );

  // Memoize selected country label lookup for performance
  const selectedLabel = useMemo(
    () => (value ? getCountryName(value) : undefined),
    [value, getCountryName]
  );

  return (
    <Select
      value={value}
      onValueChange={onValueChange}
      disabled={disabled}
      name={name}
      id={id}
    >
      <SelectTrigger
        className={className || 'min-h-[44px] w-full'}
        autoFocus={autoFocus}
        aria-required={required}
        aria-invalid={!!error}
        aria-label={name ? `${name} country selector` : 'Country selector'}
      >
        <SelectValue placeholder={defaultPlaceholder}>
          {selectedLabel}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {countryOptions.map(option => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Export default for flexibility
export default CountrySelector;
